import { Theme, ThemeMode, ThemeTokens } from '../types/theme';
import { whitelabelConfig } from '../config/whitelabel.config';
import { deepMerge } from '../utils/objects';

const activeBranding = whitelabelConfig.organizations[whitelabelConfig.activeOrganization];

// Default theme tokens that serve as a fallback
const defaultTokens: ThemeTokens = {
  colors: {
    primary: {
      50: '#f0f9ff',
      100: '#e0f2fe',
      200: '#bae6fd',
      300: '#7dd3fc',
      400: '#38bdf8',
      500: '#0ea5e9',
      600: '#0284c7',
      700: '#0369a1',
      800: '#075985',
      900: '#0c4a6e',
    },
    secondary: {
      50: '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a',
    },
    accent: {
      50: '#fff7ed',
      100: '#ffedd5',
      200: '#fed7aa',
      300: '#fdba74',
      400: '#fb923c',
      500: '#f97316',
      600: '#ea580c',
      700: '#c2410c',
      800: '#9a3412',
      900: '#7c2d12',
    },
    gray: {
      50: '#f9fafb',
      100: '#f3f4f6',
      200: '#e5e7eb',
      300: '#d1d5db',
      400: '#9ca3af',
      500: '#6b7280',
      600: '#4b5563',
      700: '#374151',
      800: '#1f2937',
      900: '#111827',
    },
    surface: {
      primary: '#ffffff',
      secondary: '#f9fafb',
      tertiary: '#f3f4f6',
    },
    text: {
      primary: '#111827',
      secondary: '#4b5563',
      tertiary: '#6b7280',
      inverse: '#ffffff',
    },
    background: {
      primary: '#ffffff',
      secondary: '#f9fafb',
      tertiary: '#f3f4f6',
    },
    border: {
      primary: '#e5e7eb',
      secondary: '#d1d5db',
      tertiary: '#9ca3af',
    },
    status: {
      success: '#22c55e',
      error: '#ef4444',
      warning: '#f59e0b',
      info: '#3b82f6',
    },
  },
  focus: {
    ring: {
      width: '1px',
      offset: '1px',
      color: 'rgb(var(--colors-primary-500) / 0.4)',
      opacity: '0.4',
    },
    transition: '200ms ease-in-out',
  },
  typography: {
    fontFamily: {
      sans: 'Inter, system-ui, -apple-system, sans-serif',
      display: 'Inter, system-ui, -apple-system, sans-serif',
      major: 'Inter, system-ui, -apple-system, sans-serif',
    },
    fontSize: {
      xs: '0.75rem',
      sm: '0.875rem',
      md: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '1.875rem',
      '4xl': '2.25rem',
    },
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
    },
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '4rem',
  },
  borderRadius: {
    none: '0',
    sm: '0.25rem',
    md: '0.5rem',
    lg: '1rem',
    full: '9999px',
  },
  shadows: {
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
  },
  transitions: {
    fast: 'all 0.1s ease-in-out',
    normal: 'all 0.2s ease-in-out',
    slow: 'all 0.3s ease-in-out',
  },
  zIndex: {
    base: 0,
    dropdown: 1000,
    sticky: 1100,
    fixed: 1200,
    modal: 1300,
    popover: 1400,
    tooltip: 1500,
  },
};

// Dark mode overrides
const darkModeOverrides: Partial<ThemeTokens> = {
  colors: {
    primary: {
      50: '#f0f9ff',
      100: '#e0f2fe',
      200: '#bae6fd',
      300: '#7dd3fc',
      400: '#38bdf8',
      500: '#0ea5e9',
      600: '#0284c7',
      700: '#0369a1',
      800: '#075985',
      900: '#0c4a6e',
    },
    secondary: {
      50: '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a',
    },
    accent: {
      50: '#fff7ed',
      100: '#ffedd5',
      200: '#fed7aa',
      300: '#fdba74',
      400: '#fb923c',
      500: '#f97316',
      600: '#ea580c',
      700: '#c2410c',
      800: '#9a3412',
      900: '#7c2d12',
    },
    gray: {
      50: '#f9fafb',
      100: '#f3f4f6',
      200: '#e5e7eb',
      300: '#d1d5db',
      400: '#9ca3af',
      500: '#6b7280',
      600: '#4b5563',
      700: '#374151',
      800: '#1f2937',
      900: '#111827',
    },
    surface: {
      primary: '#111827',
      secondary: '#1f2937',
      tertiary: '#374151',
    },
    text: {
      primary: '#f9fafb',
      secondary: '#e5e7eb',
      tertiary: '#d1d5db',
      inverse: '#111827',
    },
    background: {
      primary: '#111827',
      secondary: '#1f2937',
      tertiary: '#374151',
    },
    border: {
      primary: '#374151',
      secondary: '#4b5563',
      tertiary: '#6b7280',
    },
    status: {
      success: '#22c55e',
      error: '#ef4444',
      warning: '#f59e0b',
      info: '#3b82f6',
    }
  },
};

const getBrandingTokens = (mode: ThemeMode): Partial<ThemeTokens> => {
  const brandColors = mode === 'dark' ? activeBranding.themes.dark : activeBranding.themes.light;
  
  const generateColorScale = (baseColor: string) => ({
    50: baseColor,
    100: baseColor,
    200: baseColor,
    300: baseColor,
    400: baseColor,
    500: baseColor,
    600: baseColor,
    700: baseColor,
    800: baseColor,
    900: baseColor,
  });

  return {
    colors: {
      primary: generateColorScale(brandColors.primary),
      secondary: generateColorScale(brandColors.secondary),
      accent: generateColorScale(brandColors.accent),
      gray: generateColorScale(brandColors.text?.primary || '#111827'),
      surface: {
        primary: brandColors.surface,
        secondary: brandColors.background,
        tertiary: brandColors.border,
      },
      text: {
        primary: brandColors.text?.primary || '#111827',
        secondary: brandColors.text?.secondary || '#4B5563',
        tertiary: brandColors.border,
        inverse: mode === 'dark' ? '#111827' : '#FFFFFF',
      },
      background: {
        primary: brandColors.background,
        secondary: brandColors.surface,
        tertiary: brandColors.border,
      },
      border: {
        primary: brandColors.border,
        secondary: brandColors.border,
        tertiary: brandColors.border,
      },
      status: {
        success: brandColors.success,
        error: brandColors.error,
        warning: brandColors.warning,
        info: brandColors.success,
      },
    },
    typography: {
      fontFamily: {
        sans: activeBranding.typography.fontFamily,
        display: activeBranding.typography.headingFontFamily || activeBranding.typography.fontFamily,
        major: activeBranding.typography.majorFontFamily || activeBranding.typography.fontFamily,
      },
      fontSize: activeBranding.typography.fontSize,
      fontWeight: activeBranding.typography.fontWeight,
      lineHeight: {
        tight: 1.25,
        normal: 1.5,
        relaxed: 1.75,
      },
    },
    borderRadius: activeBranding.borderRadius ? {
      none: '0',
      sm: activeBranding.borderRadius,
      md: activeBranding.borderRadius,
      lg: activeBranding.borderRadius,
      full: '9999px',
    } : undefined,
    spacing: activeBranding.spacing ? {
      ...activeBranding.spacing,
      '2xl': activeBranding.spacing.xl || '4rem',
    } : undefined,
  };
};

export const getTheme = (mode: ThemeMode): Theme => {
  const brandingTokens = getBrandingTokens(mode);
  const modeOverrides = mode === 'dark' ? darkModeOverrides : {};
  
  const tokens = deepMerge(
    defaultTokens,
    brandingTokens,
    modeOverrides,
  ) as ThemeTokens;

  return {
    mode,
    ...tokens,
  };
}; 